import { forwardRef } from 'react'
import { cn } from './internal/utils'
import { BUTTON_SIZES, FOCUS_RING } from './internal/presets'
import { Indicator } from './Spin'

/**
 * antd v5 `type` plus the legacy `danger`/`ghost` booleans. The app's index.css
 * also styles `.ui-btn` and `.ui-btn-primary`, so those hooks are preserved.
 */
const TYPE_CLASSES = {
  primary:
    'ui-btn-primary border-[var(--color-primary-200)] text-[var(--color-primary-700)]',
  default:
    'ui-btn-default border-[var(--color-primary-200)] text-[var(--color-primary-700)]',
  dashed:
    'ui-btn-dashed border-[var(--color-primary-200)] text-[var(--color-primary-700)]',
  text: 'ui-btn-text bg-transparent border border-transparent text-[var(--color-primary-700)] hover:bg-[var(--color-primary-50)]',
  link: 'ui-btn-link bg-transparent border border-transparent text-[var(--primary)] hover:underline',
}

const DANGER_CLASSES = {
  primary: 'ui-btn-dangerous text-white border-transparent hover:opacity-90 hover:!text-white',
  default: 'ui-btn-dangerous text-white border-transparent hover:opacity-90',
  dashed: 'ui-btn-dangerous text-white border-transparent hover:opacity-90',
  text: 'text-[var(--error-color)] hover:bg-[var(--error-bg)] !bg-transparent !shadow-none',
  link: 'text-[var(--error-color)] !bg-transparent !shadow-none',
}

/** Padding and height follow the active profile's control tokens. */
const SIZE_CLASSES = BUTTON_SIZES

const SHAPE_CLASSES = {
  circle: 'rounded-full aspect-square',
  round: 'rounded-full',
  default: 'rounded-[var(--radius-sm)]',
}

/**
 * antd Button. Renders an `<a>` when `href` is set, mirrors `loading`,
 * `icon` + `iconPosition`, `block`, `shape`, `ghost` and `htmlType`.
 */
const Button = forwardRef(
  (
    {
      type = 'default',
      size = 'middle',
      shape = 'default',
      danger = false,
      ghost = false,
      block = false,
      loading = false,
      disabled = false,
      icon,
      iconPosition = 'start',
      htmlType = 'button',
      href,
      target,
      className,
      style,
      onClick,
      children,
      ...rest
    },
    ref
  ) => {
    const isLoading = Boolean(loading)
    const isDisabled = disabled || isLoading
    const isIconOnly = Boolean(icon) && children == null

    const classes = cn(
      'ui-btn inline-flex items-center justify-center gap-2 font-medium leading-none transition-colors duration-[var(--duration-fast)]',
      FOCUS_RING,
      SIZE_CLASSES[size] || SIZE_CLASSES.middle,
      SHAPE_CLASSES[shape] || SHAPE_CLASSES.default,
      TYPE_CLASSES[type] || TYPE_CLASSES.default,
      danger && (DANGER_CLASSES[type] || DANGER_CLASSES.default),
      ghost && 'ui-btn-background-ghost bg-transparent',
      block && 'ui-btn-block w-full',
      // The reference's icon button is square at the control height (`size-9`).
      isIconOnly &&
        'ui-btn-icon-only h-[var(--control-height)] w-[var(--control-height)] px-0',
      isDisabled && 'ui-btn-disabled cursor-not-allowed opacity-60 hover:!bg-inherit hover:!text-inherit',
      isLoading && 'ui-btn-loading',
      className
    )

    const content = (
      <>
        {isLoading ? <Indicator size="small" className="border-current" /> : null}
        {!isLoading && icon && iconPosition === 'start' ? (
          <span className="ui-btn-icon inline-flex">{icon}</span>
        ) : null}
        {children != null ? <span>{children}</span> : null}
        {!isLoading && icon && iconPosition === 'end' ? (
          <span className="ui-btn-icon inline-flex">{icon}</span>
        ) : null}
      </>
    )

    if (href && !isDisabled) {
      return (
        <a
          ref={ref}
          href={href}
          target={target}
          rel={target === '_blank' ? 'noreferrer' : undefined}
          className={classes}
          style={style}
          onClick={onClick}
          {...rest}
        >
          {content}
        </a>
      )
    }

    return (
      <button
        ref={ref}
        type={htmlType}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        className={classes}
        style={style}
        onClick={onClick}
        {...rest}
      >
        {content}
      </button>
    )
  }
)

Button.displayName = 'Button'
Button.Group = ({ className, children, ...rest }) => (
  <div className={cn('ui-btn-group inline-flex', className)} {...rest}>
    {children}
  </div>
)

export default Button
